import React from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { MdVideoCall } from 'react-icons/md';
import { Button } from '../ui/Kit';
import Spinner from '../ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { AppointmentTypeBadge, AppointmentStatusBadge } from './appointmentBadges';
import {
  appointmentsForDay,
  formatTimeRange,
  getInitials,
  leadDisplayName,
} from '../../lib/calendarUtils';

const { FiCalendar, FiX, FiExternalLink } = FiIcons;

function AppointmentCard({ appt, onOpen, onComplete, onCancel, onReschedule }) {
  const assigneeName = appt.assignee?.full_name || appt.assignee?.email || '—';
  const notesPreview =
    appt.notes && appt.notes.length > 80 ? `${appt.notes.slice(0, 80)}…` : appt.notes;
  const canAct = appt.status === 'scheduled' || appt.status === 'confirmed';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <button type="button" className="w-full text-left" onClick={() => onOpen(appt)}>
        <p className="text-sm font-semibold text-gray-900">{formatTimeRange(appt)}</p>
        <p className="text-sm font-medium text-primary mt-1 hover:underline">
          {appt.lead_id ? (
            <Link to={`/leads/${appt.lead_id}`} onClick={(e) => e.stopPropagation()}>
              {leadDisplayName(appt)}
            </Link>
          ) : (
            leadDisplayName(appt)
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <AppointmentTypeBadge type={appt.appointment_type} />
          <AppointmentStatusBadge status={appt.status} />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className="w-7 h-7 rounded-full bg-secondary text-white text-xs font-bold flex items-center justify-center shrink-0">
            {getInitials(assigneeName)}
          </span>
          <span className="text-xs text-gray-600">{assigneeName}</span>
        </div>
        {notesPreview && (
          <p className="text-xs text-gray-500 mt-2 line-clamp-1 border-t border-gray-100 pt-2">{notesPreview}</p>
        )}
        {appt.zoom_join_url && (
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mt-2 border-t border-gray-100 pt-2 text-xs">
            <MdVideoCall className="w-3.5 h-3.5 text-gray-500 shrink-0" aria-hidden />
            <span className="text-gray-500 font-medium">Zoom Meeting</span>
            <a
              href={appt.zoom_join_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[#00AAFF] font-medium hover:underline"
            >
              Join Zoom Meeting
              <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
            </a>
          </div>
        )}
      </button>
      {canAct && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
          <Button size="sm" variant="outline" onClick={() => onComplete(appt.id)}>
            Complete
          </Button>
          <Button size="sm" variant="outline" onClick={() => onCancel(appt.id)}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={() => onReschedule(appt)}>
            Reschedule
          </Button>
        </div>
      )}
    </div>
  );
}

export default function DayDetailPanel({
  selectedDate,
  appointments,
  loading,
  onClose,
  onNewAppointment,
  onOpenAppointment,
  onComplete,
  onCancel,
  onReschedule,
  onScheduleEmpty,
  className = '',
}) {
  const dayAppts = appointmentsForDay(appointments, selectedDate);
  const header = format(selectedDate, 'EEEE, MMMM d, yyyy');

  return (
    <aside className={`flex flex-col bg-white ${className}`}>
      <div className="flex items-start justify-between gap-2 p-4 border-b border-gray-200 shrink-0">
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-gray-900 leading-snug">{header}</h2>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" onClick={onNewAppointment}>
            + New Appointment
          </Button>
          {onClose && (
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md md:hidden"
              onClick={onClose}
              aria-label="Close day panel"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-gray-500 text-sm">
            <Spinner className="text-primary" />
            Loading…
          </div>
        ) : dayAppts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <SafeIcon icon={FiCalendar} className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">No appointments on this day</p>
            <Button size="sm" className="mt-4" onClick={onScheduleEmpty}>
              + Schedule one
            </Button>
          </div>
        ) : (
          dayAppts.map((appt) => (
            <AppointmentCard
              key={appt.id}
              appt={appt}
              onOpen={onOpenAppointment}
              onComplete={onComplete}
              onCancel={onCancel}
              onReschedule={onReschedule}
            />
          ))
        )}
      </div>
    </aside>
  );
}
